import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { IoReload } from "react-icons/io5";

// --- Haversine formula to calculate distance between 2 lat/lng in meters ---
function haversine(lat1, lon1, lat2, lon2) {
    const R = 6371000;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

const D3DistanceChart = ({ points }) => {
    const svgRef = useRef();
    const d3SvgRef = useRef(null);
    const chartContentRef = useRef(null);
    const xScaleRef = useRef(null);
    const yScaleRef = useRef(null);
    const gxRef = useRef(null);
    const gyRef = useRef(null);
    const linePathRef = useRef(null);
    const circlesRef = useRef(null);
    const polylineRef = useRef(null);
    const totalDistanceTextRef = useRef(null);
    const segmentTextsRef = useRef([]);
    const tooltipRef = useRef(null);

    const [transK, setTransK] = useState(1);
    const [selectedPoints, setSelectedPoints] = useState([]);
    const [totalDistance, setTotalDistance] = useState(null);
    const [hoveredPoint, setHoveredPoint] = useState(null);

    const initialPointRadius = 2;
    const initialStrokeWidth = 1;

    // Precompute cumulative distance (x axis)
    const data = points.map((d, i) => {
        let cumulative = 0;
        if (i === 0) return { ...d, dist: 0 };
        for (let j = 1; j <= i; j++) {
            cumulative += haversine(
                points[j - 1].lat,
                points[j - 1].lng,
                points[j].lat,
                points[j].lng
            );
        }
        return { ...d, dist: cumulative };
    });

    // --- Initial D3 Setup ---
    useEffect(() => {
        const width = 600;
        const height = 400;
        const margin = { top: 20, right: 30, bottom: 40, left: 60 };

        d3SvgRef.current = d3
            .select(svgRef.current)
            .attr("width", width)
            .attr("height", height);

        d3SvgRef.current.selectAll("*").remove();

        // Tooltip
        const tooltip = d3.select(tooltipRef.current);

        const x = d3
            .scaleLinear()
            .domain([0, d3.max(data, (d) => d.dist)])
            .range([margin.left, width - margin.right]);

        const y = d3
            .scaleLinear()
            .domain([
                d3.min(data, (d) => d.alt) - 10,
                d3.max(data, (d) => d.alt) + 10,
            ])
            .nice()
            .range([height - margin.bottom, margin.top]);

        xScaleRef.current = x;
        yScaleRef.current = y;

        chartContentRef.current = d3SvgRef.current.append("g");

        const xAxis = d3
            .axisBottom(x)
            .ticks(5)
            .tickFormat((d) => d + " m");
        const yAxis = d3
            .axisLeft(y)
            .ticks(5)
            .tickFormat((d) => d + " m");

        gxRef.current = d3SvgRef.current
            .append("g")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(xAxis);

        gyRef.current = d3SvgRef.current
            .append("g")
            .attr("transform", `translate(${margin.left},0)`)
            .call(yAxis);

        const line = d3
            .line()
            .x((d) => x(d.dist))
            .y((d) => y(d.alt));

        linePathRef.current = chartContentRef.current
            .append("path")
            .datum(data)
            .attr("fill", "none")
            .attr("stroke", "var(--secondary-1)")
            .attr("stroke-width", initialStrokeWidth)
            .attr("d", line);

        // Circles (clickable points)
        circlesRef.current = chartContentRef.current
            .selectAll("circle")
            .data(data)
            .join("circle")
            .attr("cx", (d) => x(d.dist))
            .attr("cy", (d) => y(d.alt))
            .attr("r", (initialPointRadius * 2) / transK)
            .style("cursor", "pointer")
            .on("click", (event, d) => {
                event.stopPropagation();
                setSelectedPoints((prev) => [...prev, d]); // append clicked point
            });

        // Zoom
        const zoom = d3.zoom().scaleExtent([0, 20]);
        d3SvgRef.current.call(
            zoom.on("zoom", (event) => {
                const transform = event.transform;
                setTransK(transform.k);
                const newX = transform.rescaleX(x);
                const newY = transform.rescaleY(y);
                gxRef.current.call(xAxis.scale(newX));
                gyRef.current.call(yAxis.scale(newY));
                chartContentRef.current.attr("transform", transform);
                circlesRef.current.attr("r", initialPointRadius / transform.k);
                linePathRef.current.attr(
                    "stroke-width",
                    initialStrokeWidth / transform.k
                );
            })
        );

        // --- Mousemove to find nearest point ---
        d3SvgRef.current.on("mousemove", (event) => {
            const [mx, my] = d3.pointer(event, chartContentRef.current.node());
            const transform = d3.zoomTransform(d3SvgRef.current.node());
            const invertX = transform.rescaleX(x);
            const invertY = transform.rescaleY(y);

            const mouseDist = invertX.invert(mx);
            const mouseAlt = invertY.invert(my);

            // Find nearest point
            let nearest = null;
            let minDist = Infinity;
            data.forEach((p) => {
                const dx = x(p.dist) - mx;
                const dy = y(p.alt) - my;
                const d2 = dx * dx + dy * dy;
                if (d2 < minDist) {
                    minDist = d2;
                    nearest = p;
                }
            });
            tooltip
                .style("opacity", 1)
                .html(
                    `<strong>Lat</strong>: ${nearest.lat.toFixed(
                        4
                    )}<br><strong>Lng</strong>: ${nearest.lng.toFixed(4)}<br><strong>Alt</strong>: ${
                        (nearest.alt).toFixed(8)
                    }m`
                )
                .style("left", 10 + "px")
                .style("top",  10 + "px");
            setHoveredPoint(nearest);
        });
    }, [points]);

    // --- Update styles on hover/selection ---
    useEffect(() => {
        if (!circlesRef.current) return;
        circlesRef.current
            .attr("fill", (d) => {
                if (selectedPoints.includes(d)) return "orange";
                if (hoveredPoint === d) return "yellow";
                return "var(--secondary-2)";
            })
            .attr("r", (d) => {
                if (hoveredPoint === d)
                    return (initialPointRadius * 3) / transK;
                if (selectedPoints.includes(d))
                    return (initialPointRadius * 2) / transK;
                // return (initialPointRadius * 2) / transK;
            });
    }, [selectedPoints, hoveredPoint, transK]);

    // --- Draw polyline, segment labels & total distance ---
    useEffect(() => {
        if (!chartContentRef.current) return;

        if (polylineRef.current) polylineRef.current.remove();
        if (totalDistanceTextRef.current) totalDistanceTextRef.current.remove();
        segmentTextsRef.current.forEach((t) => t.remove());
        segmentTextsRef.current = [];

        if (selectedPoints.length >= 2) {
            // Draw polyline
            const polyline = d3
                .line()
                .x((d) => xScaleRef.current(d.dist))
                .y((d) => yScaleRef.current(d.alt));

            polylineRef.current = chartContentRef.current
                .append("path")
                .datum(selectedPoints)
                .attr("fill", "none")
                .attr("stroke", "orange")
                .attr("stroke-width", (initialStrokeWidth * 2) / transK)
                .attr("d", polyline);

            // Calculate total + segment distances
            let total = 0;
            for (let i = 1; i < selectedPoints.length; i++) {
                const p1 = selectedPoints[i - 1];
                const p2 = selectedPoints[i];
                const segDist = Math.sqrt(
                    Math.pow(p2.dist - p1.dist, 2) +
                        Math.pow(p2.alt - p1.alt, 2)
                );
                total += segDist;

                // Add segment distance text at midpoint
                const midX =
                    (xScaleRef.current(p1.dist) + xScaleRef.current(p2.dist)) /
                    2;
                const midY =
                    (yScaleRef.current(p1.alt) + yScaleRef.current(p2.alt)) / 2;
                const segText = chartContentRef.current
                    .append("text")
                    .attr("x", midX)
                    .attr("y", midY)
                    .attr("text-anchor", "middle")
                    .attr("fill", "white")
                    .attr("stroke", "black")
                    .attr("stroke-width", 0.3 / transK)
                    .style("font-size", `${14 / transK}px`)
                    .style("font-weight", "bold")
                    .text(`${segDist.toFixed(2)}m`);
                segmentTextsRef.current.push(segText);
            }
            setTotalDistance(total.toFixed(2));

            // Place total distance label at last point
            const last = selectedPoints[selectedPoints.length - 1];
            totalDistanceTextRef.current = chartContentRef.current
                .append("text")
                .attr("x", xScaleRef.current(last.dist) + 10)
                .attr("y", yScaleRef.current(last.alt) - 10)
                .attr("fill", "white")
                .attr("stroke", "black")
                .attr("stroke-width", 0.4 / transK)
                .style("font-size", `${16 / transK}px`)
                .style("font-weight", "bold")
                .text(`Σ ${total.toFixed(2)}m`);
        } else {
            setTotalDistance(null);
        }
    }, [selectedPoints, transK]);

    return (
        <div
            className="flex flex-col items-center p-4 relative"
            style={{ backgroundColor: "var(--primary-2)" }}
        >
            <h5 className="text-xl font-bold mb-4" style={{fontWeight:700}}>
                Altitude over Distance
            </h5>
            <div className="d-flex gap-3 justify-content-center">
                <button
                    onClick={() => {
                        setSelectedPoints([]);
                        setTotalDistance(null);
                        if (polylineRef.current) polylineRef.current.remove();
                        if (totalDistanceTextRef.current)
                            totalDistanceTextRef.current.remove();
                        segmentTextsRef.current.forEach((t) => t.remove());
                        segmentTextsRef.current = [];
                    }}
                    className="btn cyan-theme"
                    title="Reset Distance"
                >
                    <IoReload />
                </button>
            </div>
            <div className="p-6 rounded-lg shadow-md">
                <svg ref={svgRef} width="100%"></svg>
            </div>
            <div
                ref={tooltipRef}
                style={{
                    position: "absolute",
                    textAlign: "left",
                    padding: "5px",
                    fontSize: "12px",
                    color: "var(--secondary-1)",
                    borderRadius: "4px",
                    pointerEvents: "none",
                    opacity: 0,
                    border:"0.5px solid var(--secondary-2)"
                }}
            />
        </div>
    );
};

export default D3DistanceChart;
